// Fisica del caos del mucchietto — stato REALE delle carte.
// Regole vincolanti:
// - tutte le carte partono DRETTE (reversed: false), sempre;
// - l'orientamento cambia SOLO per capovolgimento fisico durante il caos:
//   una carta si gira quando viene travolta dallo scivolamento prolungato di una carta che le passa sopra;
// - nessuna quota, nessun bilanciamento, nessuna rotazione artificiale, nessun random sugli orientamenti;
// - identita', ordine e orientamento restano attaccati alla carta in ogni fase.

export const CHAOS_CFG = {
  RADIUS: 130,    // raggio di influenza della mano (px)
  DRAG: 0.9,      // quanto le carte seguono il dito (smorzato -> movimento lento e fluido)
  MAX_STEP: 7,    // velocita' massima di una carta per frame: il caos resta lento anche se il dito corre
  FLIP_SHEAR: 70, // scivolamento relativo cumulato (px) necessario a capovolgere fisicamente una carta
  CARD_W: 46,
  CARD_H: 66,
};

export const createChaos = (ids, W, H) => {
  // mucchietto LARGO e distribuito: carte sparse su un'ampia superficie, tutte dritte
  const cards = ids.map((id, i) => {
    const a = Math.random() * Math.PI * 2;
    const r = Math.pow(Math.random(), 0.6);
    return {
      id,
      reversed: false,
      z: i,
      shear: 0,
      lastMove: 0,
      x: W / 2 + Math.cos(a) * r * W * 0.34 - CHAOS_CFG.CARD_W / 2,
      y: H / 2 + Math.sin(a) * r * H * 0.3 - CHAOS_CFG.CARD_H / 2,
      angle: (Math.random() * 2 - 1) * 18,
    };
  });
  return { cards, zCounter: cards.length, prevAng: null, totalAng: 0, laps: 0 };
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const chaosStep = (st, px, py, dx, dy, cx, cy) => {
  const { RADIUS, DRAG, MAX_STEP, FLIP_SHEAR, CARD_W, CARD_H } = CHAOS_CFG;
  const W = cx * 2;
  const H = cy * 2;

  // conteggio dei tre giri lenti attorno al centro del mucchietto
  const ang = Math.atan2(py - cy, px - cx);
  if (st.prevAng !== null) {
    let d = ang - st.prevAng;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    st.totalAng += d;
    st.laps = Math.min(3, Math.floor(Math.abs(st.totalAng) / (Math.PI * 2)));
  }
  st.prevAng = ang;

  const speed = Math.hypot(dx, dy);
  if (!speed) return [];
  const stepLen = Math.min(speed, MAX_STEP);
  // fisica guidata dai PIXEL percorsi, non dal numero/frequenza degli eventi pointermove
  const moveRatio = stepLen / MAX_STEP;
  const ux = dx / speed;
  const uy = dy / speed;

  // 1) trascinamento: le carte nel raggio della mano seguono il gesto, piu' forte vicino al dito
  for (const c of st.cards) {
    const ddx = c.x + CARD_W / 2 - px;
    const ddy = c.y + CARD_H / 2 - py;
    const dist = Math.hypot(ddx, ddy);
    if (dist > RADIUS) {
      c.lastMove = 0;
      continue;
    }
    const inf = Math.pow(1 - dist / RADIUS, 1.4);
    const mx = ux * stepLen * DRAG * inf;
    const my = uy * stepLen * DRAG * inf;
    c.x += mx;
    c.y += my;
    // la spinta laterale inclina un poco la carta (effetto fisico, non capovolgimento)
    c.angle += ((ux * -ddy + uy * ddx) / (dist + 40)) * inf * 30;
    c.lastMove = Math.hypot(mx, my);
    if (inf > 0.3) c.z = ++st.zCounter; // la carta smossa dalla mano sale sopra le altre
  }

  // 2) interazione tra carte: le sovrapposizioni si spingono; chi viene travolta puo' capovolgersi
  const flips = [];
  const cards = st.cards;
  for (let iter = 0; iter < 2; iter++) {
    for (let i = 0; i < cards.length; i++) {
      for (let j = i + 1; j < cards.length; j++) {
        const a = cards[i];
        const b = cards[j];
        const ox = CARD_W * 0.85 - Math.abs(a.x - b.x);
        const oy = CARD_H * 0.85 - Math.abs(a.y - b.y);
        if (ox <= 0 || oy <= 0) continue;
        const top = a.z >= b.z ? a : b;
        const bot = a.z >= b.z ? b : a;
        const depth = Math.min(ox, oy);
        const dirx = bot.x < top.x ? -1 : 1;
        const diry = bot.y < top.y ? -1 : 1;
        // separazione e spinta proporzionali alla strada realmente percorsa dalla mano
        bot.x += dirx * depth * 0.08 * moveRatio;
        bot.y += diry * depth * 0.08 * moveRatio;
        bot.angle += dirx * depth * 0.15 * moveRatio;
        // attrito di scivolamento in pixel reali: se si accumula abbastanza, la carta sotto si capovolge fisicamente
        if (top.lastMove > 0) {
          bot.shear += top.lastMove * (depth / CARD_W) * 0.5;
          if (bot.shear >= FLIP_SHEAR) {
            bot.shear = 0;
            bot.reversed = !bot.reversed;
            bot.z = ++st.zCounter; // il capovolgimento solleva la carta sopra il mucchio
            flips.push(bot.id);
          }
        }
      }
    }
  }

  // 3) contenimento sul tavolo
  for (const c of st.cards) {
    c.x = clamp(c.x, 2, W - CARD_W - 2);
    c.y = clamp(c.y, 2, H - CARD_H - 2);
    c.angle = clamp(c.angle, -75, 75);
  }
  return flips;
};

// Ricomposizione: il mazzo finale e' lo STESSO mazzo trasformato.
// order[0] = cima del mazzo (carta con z piu' alto); orientamento ed identita' seguono la carta.
export const finalizeChaos = (st) =>
  [...st.cards]
    .sort((a, b) => b.z - a.z)
    .map((c) => ({ id: c.id, reversed: c.reversed }));

// --- vere operazioni di mischiatura sull'ordine reale (agiscono sulle carte, orientamento incluso) ---
export const riffle = (d) => {
  const mid = Math.floor(d.length / 2) + (Math.random() < 0.5 ? -2 : 2);
  const a = d.slice(0, mid);
  const b = d.slice(mid);
  const out = [];
  while (a.length || b.length) {
    const src = Math.random() < 0.5 ? (a.length ? a : b) : (b.length ? b : a);
    const k = Math.min(src.length, 1 + Math.floor(Math.random() * 3));
    out.push(...src.splice(0, k));
  }
  return out;
};
export const overhand = (d) => {
  const out = [];
  let rest = [...d];
  while (rest.length) {
    const k = Math.min(rest.length, 2 + Math.floor(Math.random() * 5));
    out.unshift(...rest.splice(0, k));
  }
  return out;
};
export const cutOp = (d) => {
  const i = 4 + Math.floor(Math.random() * (d.length - 8));
  return [...d.slice(i), ...d.slice(0, i)];
};
export const SHUFFLE_OPS = [riffle, overhand, cutOp, riffle];
